// client/src/components/stats/VpipBreakdown.tsx
//
// Muestra el breakdown de VPIP por posición (EP, HJ, CO, BTN, SB, BB).
// Sigue el mismo patrón que RfiBreakdown — siempre emite las 6 posiciones
// aunque alguna tenga opp=0.

import React from 'react';
import { VpipDerived } from '../../utils/pokerStats/types';
import { MIN_VPIP_SAMPLE } from '../../utils/pokerStats/constants';
import { useT } from '../../lib/i18n';
import { useBenchmarks } from '../../contexts/BenchmarkContext';

interface VpipBreakdownProps {
  vpip: VpipDerived[];
  label?: string;
}

export function VpipBreakdown({ vpip, label }: VpipBreakdownProps) {
  const { t } = useT();
  const bm = useBenchmarks();

  const totalOpp = vpip.reduce((s, v) => s + v.opp, 0);

  if (totalOpp === 0) {
    return (
      <div className="rfi-breakdown rfi-empty">
        <p className="rfi-empty-text">{t('stats.vpip.empty')}</p>
      </div>
    );
  }

  // ¿Hay al menos una posición con sample suficiente?
  const anySufficient = vpip.some(v => !v.lowSample && v.opp > 0);

  return (
    <div className="rfi-breakdown">
      {label && <div className="rfi-breakdown-title">{label}</div>}

      {!anySufficient && (
        <div className="rfi-warning">{t('stats.vpip.warning', { n: MIN_VPIP_SAMPLE })}</div>
      )}

      <table className="rfi-table">
        <thead>
          <tr>
            <th>{t('stats.vpip.thPosition')}</th>
            <th className="rfi-th-num">{t('stats.vpip.thVpip')}</th>
            <th className="rfi-th-num">{t('stats.vpip.thBenchmark')}</th>
            <th className="rfi-th-num">{t('stats.vpip.thSample')}</th>
            <th className="rfi-th-status" />
          </tr>
        </thead>
        <tbody>
          {vpip.map(v => {
            const benchmark = bm.getVpipBenchmark(v.position);
            const status = v.lowSample || v.opp === 0 ? 'ok' : bm.evalVpip(v.position, v.pct);
            const showEval = !v.lowSample && v.opp > 0 && benchmark;

            return (
              <tr
                key={v.position}
                className={`rfi-row rfi-row-${status} ${v.lowSample ? 'rfi-row-low' : ''}`}
              >
                <td className="rfi-cell-pos">{v.position}</td>
                <td className="rfi-cell-num rfi-cell-value">
                  {v.opp > 0 ? `${v.pct}%` : '—'}
                </td>
                <td className="rfi-cell-num rfi-cell-benchmark">
                  {benchmark ? `${benchmark.min}–${benchmark.max}%` : '—'}
                </td>
                <td className="rfi-cell-num rfi-cell-sample">
                  {v.opp > 0 ? `n=${v.opp}` : '—'}
                  {v.lowSample && v.opp > 0 && (
                    <span className="rfi-low-tag"> {t('stats.vpip.lowTag')}</span>
                  )}
                </td>
                <td className="rfi-cell-status">
                  {showEval && <span className={`stat-dot dot-${status}`} />}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
